import { useEffect } from 'react';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateOnly(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatAmount(amount) {
  return parseFloat(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getStatusBadgeClass(status) {
  if (status === 'pending') return 'status-badge--pending';
  if (status === 'completed' || status === 'processed') return 'status-badge--success';
  if (status === 'failed' || status === 'error') return 'status-badge--error';
  return 'status-badge--pending';
}

function getSalesforceOpportunityUrl(opportunityId) {
  if (!opportunityId) return null;
  return `https://lightning.force.com/lightning/r/Opportunity/${opportunityId}/view`;
}

export default function QueueDrawer({ item, onClose }) {
  const open = item !== null;
  const invoiceData = item?.invoice_data ?? {};
  const opportunity = item?.sf_opportunity ?? {};
  const account = opportunity.Account ?? {};
  const lineItems = item?.line_items ?? [];
  const salesforceUrl = getSalesforceOpportunityUrl(item?.sf_opportunity_id);
  const totalAmount = lineItems.reduce((sum, li) => sum + (parseFloat(li.Amount) || 0), 0);

  useEffect(() => {
    if (!open) return undefined;
    document.documentElement.classList.add('drawer-open');
    return () => document.documentElement.classList.remove('drawer-open');
  }, [open]);

  return (
    <>
      <div
        className={`drawer-overlay${open ? ' open' : ''}`}
        onClick={onClose}
      />
      <aside className={`drawer${open ? ' open' : ''}`}>
        {item && (
          <>
            <div className="drawer-header">
              <div className="drawer-header-left">
                <span className="drawer-event-id">#{item.id}</span>
                <span className={`status-badge ${getStatusBadgeClass(item.status)}`}>
                  {item.status}
                </span>
              </div>
              <button className="drawer-close" onClick={onClose} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="drawer-title-block">
              <h2 className="drawer-event-type">{invoiceData.deal_name ?? opportunity.Name ?? 'Queued Invoice'}</h2>
              <p className="drawer-event-date">
                Scheduled for {formatDateOnly(item.future_date)}
              </p>
            </div>

            <div className="drawer-body">
              {salesforceUrl && (
                <div className="drawer-external-links">
                  <a
                    href={salesforceUrl}
                    className="btn btn-secondary drawer-external-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View in Salesforce
                  </a>
                </div>
              )}

              <div className="drawer-section">
                <div className="drawer-section-title">Invoice</div>
                <div className="drawer-kv-grid">
                  {invoiceData.customer_name && (
                    <div className="drawer-kv drawer-kv--full">
                      <span className="drawer-kv-label">Customer</span>
                      <span className="drawer-kv-value">{invoiceData.customer_name}</span>
                    </div>
                  )}
                  {invoiceData.deal_name && (
                    <div className="drawer-kv drawer-kv--full">
                      <span className="drawer-kv-label">Deal</span>
                      <span className="drawer-kv-value">{invoiceData.deal_name}</span>
                    </div>
                  )}
                  {invoiceData.term_year != null && (
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Term Year</span>
                      <span className="drawer-kv-value">Year {invoiceData.term_year}</span>
                    </div>
                  )}
                  {lineItems.length > 0 && (
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Total</span>
                      <span className="drawer-kv-value">${formatAmount(totalAmount)}</span>
                    </div>
                  )}
                  {item.process_type && (
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Process Type</span>
                      <span className="drawer-kv-value">{item.process_type}</span>
                    </div>
                  )}
                </div>
              </div>

              {(opportunity.Name || account.Name) && (
                <div className="drawer-section">
                  <div className="drawer-section-title">Opportunity</div>
                  <div className="drawer-kv-grid">
                    {account.Name && (
                      <div className="drawer-kv drawer-kv--full">
                        <span className="drawer-kv-label">Account</span>
                        <span className="drawer-kv-value">{account.Name}</span>
                      </div>
                    )}
                    {opportunity.Name && (
                      <div className="drawer-kv drawer-kv--full">
                        <span className="drawer-kv-label">Opportunity</span>
                        <span className="drawer-kv-value">{opportunity.Name}</span>
                      </div>
                    )}
                    {opportunity.CloseDate && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Close Date</span>
                        <span className="drawer-kv-value">{formatDateOnly(opportunity.CloseDate)}</span>
                      </div>
                    )}
                    {opportunity.Actual_Close_Date__c && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Actual Close</span>
                        <span className="drawer-kv-value">{formatDateOnly(opportunity.Actual_Close_Date__c)}</span>
                      </div>
                    )}
                    {opportunity.Term_Years__c != null && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Term Years</span>
                        <span className="drawer-kv-value">{opportunity.Term_Years__c}</span>
                      </div>
                    )}
                    {opportunity.Opportunity_Channel__c && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Channel</span>
                        <span className="drawer-kv-value">{opportunity.Opportunity_Channel__c}</span>
                      </div>
                    )}
                    {opportunity.Billing_Email__c && (
                      <div className="drawer-kv drawer-kv--full">
                        <span className="drawer-kv-label">Billing Email</span>
                        <span className="drawer-kv-value">{opportunity.Billing_Email__c}</span>
                      </div>
                    )}
                    {item.sf_opportunity_id && (
                      <div className="drawer-kv drawer-kv--full">
                        <span className="drawer-kv-label">Opportunity ID</span>
                        <span className="drawer-kv-value drawer-kv-mono">{item.sf_opportunity_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {lineItems.length > 0 && (
                <div className="drawer-section">
                  <div className="drawer-section-title">Line Items</div>
                  <div className="event-line-items">
                    {lineItems.map((lineItem, i) => (
                      <div key={i} className="event-line-item">
                        <span className="event-line-name">{lineItem.Description}</span>
                        <span className="event-line-amount">${formatAmount(lineItem.Amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="drawer-section">
                <div className="drawer-section-title">Schedule</div>
                <div className="drawer-kv-grid">
                  <div className="drawer-kv">
                    <span className="drawer-kv-label">Future Date</span>
                    <span className="drawer-kv-value">{formatDateOnly(item.future_date)}</span>
                  </div>
                  {item.future_month != null && (
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Future Month</span>
                      <span className="drawer-kv-value">{item.future_month}</span>
                    </div>
                  )}
                  {item.future_year != null && (
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Future Year</span>
                      <span className="drawer-kv-value">Year {item.future_year}</span>
                    </div>
                  )}
                  {item.term_years != null && (
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Term Years</span>
                      <span className="drawer-kv-value">{item.term_years}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="drawer-section">
                <div className="drawer-section-title">Timestamps</div>
                <div className="drawer-kv-grid">
                  <div className="drawer-kv">
                    <span className="drawer-kv-label">Created</span>
                    <span className="drawer-kv-value">{formatDate(item.create_date)}</span>
                  </div>
                  <div className="drawer-kv">
                    <span className="drawer-kv-label">Updated</span>
                    <span className="drawer-kv-value">{formatDate(item.update_date)}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
